import type { AxiosRequestConfig } from 'axios';

const SOFT_HYPHEN_RE = /\u00AD/g;
const BAD_DASHES_RE = /[\u2010\u2011\u2012\u2013\u2014\u2015\u2212\uFE63\uFF0D\u2043\u02D7]/g;
// Covers common Word/Office and copy-paste dash/minus variants:
// U+2010..U+2015 (hyphen/en-dash/em-dash/etc), U+2212 (minus sign),
// plus a few rarer “hyphen-like” unicode forms.

export const normalizeMsWordDashes = (input: string): string => {
  if (!input) return input;

  // NFC keeps combined characters consistent; cheap and safe.
  const s = input.normalize('NFC');

  // Remove soft hyphen, normalize other dash-like chars to "-"
  return s.replace(SOFT_HYPHEN_RE, '').replace(BAD_DASHES_RE, '-');
};

/**
 * MS Office can inject “smart dashes” (en/em-dash, minus sign, etc.) into pasted text.
 * Those characters can break downloads (S3 Content-Disposition), URL/path matching, etc.
 *
 * This sanitizer normalizes them to ASCII "-" and removes soft hyphen.
 */

const isPlainObject = (value: unknown): value is Record<string, unknown> => {
  if (value === null || typeof value !== 'object') return false;
  const proto = Object.getPrototypeOf(value);
  return proto === Object.prototype || proto === null;
};

export const sanitizeMsWordDashesDeep = <T>(
  value: T,
  // eslint-disable-next-line @typescript-eslint/ban-types
  seen = new WeakSet<object>()
): T => {
  if (typeof value === 'string') {
    return (normalizeMsWordDashes(value) as unknown) as T;
  }

  if (value === null || value === undefined) return value;
  if (typeof value !== 'object') return value;

  const anyVal = value as any;

  // Do not touch upload/binary/special payload types here.
  // FormData is handled separately because we DO want to sanitize filenames there.
  if (typeof FormData !== 'undefined' && anyVal instanceof FormData)
    return value;
  if (typeof File !== 'undefined' && anyVal instanceof File) return value;
  if (typeof Blob !== 'undefined' && anyVal instanceof Blob) return value;
  if (typeof ArrayBuffer !== 'undefined' && anyVal instanceof ArrayBuffer)
    return value;
  if (
    typeof URLSearchParams !== 'undefined' &&
    anyVal instanceof URLSearchParams
  )
    return value;
  if (anyVal instanceof Date) return value;

  // Protect against cyclic refs
  if (seen.has(anyVal)) return value;
  seen.add(anyVal);

  if (Array.isArray(value)) {
    //@ts-ignore
    return (value.map((v) =>
      sanitizeMsWordDashesDeep(v, seen)
    ) as unknown) as T;
  }

  // Only sanitize plain {} objects (avoid class instances)
  if (!isPlainObject(value)) return value;

  const out: Record<string, unknown> = {};
  Object.keys(value as Record<string, unknown>).forEach((k) => {
    out[k] = sanitizeMsWordDashesDeep(
      (value as Record<string, unknown>)[k],
      seen
    );
  });

  return (out as unknown) as T;
};

export const sanitizeFormDataMsWordDashes = (form: FormData): FormData => {
  const newForm = new FormData();

  form.forEach((value, key) => {
    // Strings inside FormData
    if (typeof value === 'string') {
      newForm.append(key, normalizeMsWordDashes(value));
      return;
    }

    // Files inside FormData — sanitize filename ONLY (do not recreate File)
    if (typeof File !== 'undefined' && value instanceof File) {
      const safeName = normalizeMsWordDashes(value.name);
      newForm.append(key, value, safeName);
      return;
    }

    // Any other values (Blob etc.) untouched
    newForm.append(key, value as any);
  });

  return newForm;
};

/**
 * Sanitizes axios request config:
 * - params: deep sanitize (strings)
 * - data: deep sanitize for JSON-like objects OR sanitize FormData (filenames + string fields)
 */
export const sanitizeAxiosConfigMsWordDashes = (
  config: AxiosRequestConfig
): AxiosRequestConfig => {
  if (config.params !== undefined) {
    config.params = sanitizeMsWordDashesDeep(config.params);
  }

  if (config.data !== undefined) {
    // console.log(`sanitizer #1`, config.data);
    const anyData = config.data as any;

    if (typeof FormData !== 'undefined' && anyData instanceof FormData) {
      config.data = sanitizeFormDataMsWordDashes(anyData);
    } else {
      config.data = sanitizeMsWordDashesDeep(config.data);
    }

    // console.log(`sanitizer #2`, config.data);
  }

  return config;
};
